import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Search, ArrowUpDown, X, RotateCcw, Filter, Sun, Moon,
  Layers, ChevronDown, EyeOff, ArrowLeft
} from 'lucide-react';
import { api, type CellStyle, type PublicDoc } from '../lib/api';
import { Grid } from '../components/Grid';
import { Spinner } from '../components/Spinner';
import { useComputed } from '../lib/formula/useComputed';
import { useView, uniqueValues, type Sort } from '../lib/useView';
import { toggle, useTheme } from '../lib/theme';
import { R, ft, shadow, label, chip, btnAccent, btnOutline } from '../lib/ui';
import { Select } from '../components/Select';

/**
 * Полноценный экран просмотра таблицы (как по открытой ссылке /shared/:token,
 * так и по публичному снимку /p/:token):
 * - Поиск по таблице (Ctrl+F) и фильтрация
 * - Сортировка по колонкам (А-Я / Я-А)
 * - Фильтры по колонкам с выбором значений
 * - Плавная прокрутка (ролл) колёсиком мыши и клавиатурой
 * - Строка активных фильтров и статусная строка Redwood Tables
 */
export function PublicView({ token, kind }: { token: string; kind: 'shared' | 'public' }) {
  const theme = useTheme();
  const [activeId, setActiveId] = useState<string | null>(null);

  // Поиск, сортировка и фильтрация
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<Sort>(null);
  const [showSearch, setShowSearch] = useState(false);
  const [searchColId, setSearchColId] = useState<string | null>(null);
  const [filters, setFilters] = useState<Map<string, Set<string>>>(new Map());

  const doc = useQuery({
    queryKey: [kind, token],
    queryFn: () => (kind === 'shared' ? api.sharedDoc(token) : api.publicDoc(token)),
  });

  const tables = doc.data?.tables ?? [];
  const table = tables.find((t) => t.id === activeId) ?? tables[0];

  const styles = useMemo(() => {
    const map = new Map<string, CellStyle>();
    for (const s of table?.styles ?? []) map.set(`${s.recordId}:${s.colId}`, s.style);
    return map;
  }, [table]);

  const allSheets = useMemo(() => {
    if (tables.length <= 1) return undefined;
    return tables.map((t) => ({
      id: t.id,
      name: t.label,
      columns: t.columns,
      records: t.records,
    }));
  }, [tables]);

  const computed = useComputed(
    table?.columns ?? [],
    table?.records ?? [],
    allSheets,
    table?.label,
  );

  // Фильтрованное и отсортированное представление строк
  const view = useView(
    table?.columns ?? [],
    table?.records ?? [],
    query,
    sort,
    computed,
    searchColId,
    filters,
  );

  const isFilteredOrSorted = Boolean(query.trim() || sort || filters.size > 0);

  const colWidths = useMemo(
    () =>
      new Map(
        (table?.sizes.colWidths ?? [])
          .filter((c): c is { colId: string; width: number } => c.width !== null)
          .map((c) => [c.colId, c.width]),
      ),
    [table],
  );

  const rowHeights = useMemo(
    () =>
      new Map(
        (table?.sizes.rowHeights ?? [])
          .filter((r): r is { recordId: string; height: number } => r.height !== null)
          .map((r) => [r.recordId, r.height]),
      ),
    [table],
  );

  // Горячая клавиша Ctrl+F / Cmd+F для открытия поиска по таблице
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.code === 'KeyF') {
        e.preventDefault();
        setShowSearch(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const setFilter = (colId: string, next: Set<string> | null) => {
    setFilters((prev) => {
      const map = new Map(prev);
      if (next) map.set(colId, next);
      else map.delete(colId);
      return map;
    });
  };

  const valuesFor = (colId: string) => {
    const col = table?.columns.find((c) => c.fields.colId === colId);
    return uniqueValues(col, table?.records ?? [], computed);
  };

  const getColLabel = (colId: string) => {
    return table?.columns.find((c) => c.fields.colId === colId)?.fields.label ?? colId;
  };

  const [hiddenColIds, setHiddenColIds] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(`rt-hidden-cols:pub:${table?.id ?? ''}`);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    if (!table?.id) return;
    try {
      const raw = localStorage.getItem(`rt-hidden-cols:pub:${table.id}`);
      setHiddenColIds(raw ? new Set(JSON.parse(raw)) : new Set());
    } catch {
      setHiddenColIds(new Set());
    }
  }, [table?.id]);

  const toggleHideColumn = (colId: string) => {
    setHiddenColIds((prev) => {
      const next = new Set(prev);
      if (next.has(colId)) next.delete(colId);
      else next.add(colId);
      if (table?.id) {
        try {
          localStorage.setItem(`rt-hidden-cols:pub:${table.id}`, JSON.stringify([...next]));
        } catch {}
      }
      return next;
    });
  };

  const unhideAllColumns = () => {
    setHiddenColIds(new Set());
    if (table?.id) {
      try {
        localStorage.removeItem(`rt-hidden-cols:pub:${table.id}`);
      } catch {}
    }
  };

  const unhideColumns = (colIds: string[]) => {
    setHiddenColIds((prev) => {
      const next = new Set(prev);
      for (const id of colIds) next.delete(id);
      if (table?.id) {
        try {
          localStorage.setItem(`rt-hidden-cols:pub:${table.id}`, JSON.stringify([...next]));
        } catch {}
      }
      return next;
    });
  };

  const resetAllFilters = () => {
    setQuery('');
    setSort(null);
    setSearchColId(null);
    setFilters(new Map());
    setShowSearch(false);
    unhideAllColumns();
  };

  if (doc.isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: 12,
          color: R.textMuted,
          fontSize: 14,
          background: R.bg,
        }}
      >
        <Spinner size={24} />
        <span>Загрузка данных таблицы Redwood…</span>
      </div>
    );
  }

  if (doc.error) {
    return (
      <div
        style={{
          display: 'grid',
          placeItems: 'center',
          padding: 40,
          minHeight: '100vh',
          textAlign: 'center',
          background: R.bg,
        }}
      >
        <div style={{ maxWidth: 460 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: R.danger, marginBottom: 8 }}>
            {(doc.error as Error).message}
          </div>
          <div style={{ fontSize: 13, color: R.textMuted }}>
            Ссылка недоступна или была отозвана. Обратитесь к администратору для получения актуальной ссылки.
          </div>
        </div>
      </div>
    );
  }

  const data = doc.data as PublicDoc;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100%',
        overflow: 'hidden',
        background: R.bg,
        color: R.text,
        fontFamily: 'Archivo, system-ui, -apple-system, sans-serif',
      }}
    >
      {/* ---------- ВЕРХНЯЯ ШАПКА ---------- */}
      <header
        style={{
          height: 48,
          background: R.bgSubtle,
          borderBottom: ft.edge,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 16px',
          flex: '0 0 auto',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <a
            href="/portal"
            title="Вернуться на портал государственных реестров"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 28,
              padding: '0 10px',
              borderRadius: 5,
              border: ft.edge,
              background: R.accentSubtle,
              color: R.accentText,
              fontSize: 12,
              fontWeight: 700,
              textDecoration: 'none',
              cursor: 'pointer',
              transition: 'all 0.12s ease',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = R.accent;
              e.currentTarget.style.color = R.onAccent;
              e.currentTarget.style.transform = 'translateX(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = R.accentSubtle;
              e.currentTarget.style.color = R.accentText;
              e.currentTarget.style.transform = 'none';
            }}
          >
            <ArrowLeft size={13} />
            <span>Назад на портал</span>
          </a>

          <div style={{ width: 1, height: 20, background: R.border, flexShrink: 0, margin: '0 2px' }} />

          <img
            src="/favicon.png"
            alt="Redwood Tables"
            width={26}
            height={26}
            style={{
              width: 26,
              height: 26,
              borderRadius: 5,
              objectFit: 'contain',
              display: 'block',
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontWeight: 800,
              fontSize: 14,
              letterSpacing: '-0.015em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {data.name}
          </span>

          <span
            style={{
              padding: '2px 8px',
              borderRadius: 4,
              background: R.bgInput,
              border: ft.hair,
              color: R.textMuted,
              fontSize: 11,
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            Только чтение
          </span>
        </div>

        {/* ПАНЕЛЬ ИНСТРУМЕНТОВ: ПОИСК, СОРТИРОВКА, ТЕМА */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            onClick={() => setShowSearch((v) => !v)}
            title="Поиск по таблице (Ctrl + F)"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              height: 28,
              padding: '0 10px',
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 4,
              border: ft.edge,
              background: showSearch || query ? R.accentSubtle : R.bgInput,
              color: showSearch || query ? R.accentText : R.text,
              cursor: 'pointer',
              transition: 'all 0.12s ease',
            }}
          >
            <Search size={13} />
            <span>Поиск</span>
            <span style={{ fontSize: 10, opacity: 0.65, marginLeft: 2 }}>Ctrl+F</span>
          </button>

          {isFilteredOrSorted && (
            <button
              onClick={resetAllFilters}
              title="Сбросить все фильтры, сортировку и поиск"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                height: 28,
                padding: '0 8px',
                fontSize: 12,
                borderRadius: 4,
                border: ft.edge,
                background: R.dangerSubtle,
                color: R.danger,
                cursor: 'pointer',
              }}
            >
              <RotateCcw size={12} />
              <span>Сбросить</span>
            </button>
          )}

          <button
            onClick={() => toggle()}
            title="Переключить тему"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              borderRadius: 4,
              border: ft.edge,
              background: R.bgInput,
              color: R.text,
              cursor: 'pointer',
            }}
          >
            {theme === 'dark' ? <Sun size={13} /> : <Moon size={13} />}
          </button>
        </div>
      </header>

      {/* ---------- ВЫБОР ЛИСТОВ / ТАБЛИЦ ---------- */}
      {tables.length > 1 && (
        <div
          className="rt-scroll"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            height: 34,
            padding: '0 16px',
            background: R.bgSubtle,
            borderBottom: ft.hair,
            flex: '0 0 auto',
            overflowX: 'auto',
          }}
        >
          {tables.map((t) => {
            const active = t.id === table?.id;
            return (
              <button
                key={t.id}
                onClick={() => {
                  setActiveId(t.id);
                  resetAllFilters();
                }}
                style={{
                  padding: '4px 12px',
                  fontSize: 12,
                  fontWeight: active ? 700 : 500,
                  border: 'none',
                  borderRadius: 4,
                  background: active ? R.accent : 'transparent',
                  color: active ? R.onAccent : R.textSecondary,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.12s ease',
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      )}

      {/* ---------- СТРОКА АКТИВНЫХ ФИЛЬТРОВ И ПОИСКА ---------- */}
      {(isFilteredOrSorted || hiddenColIds.size > 0) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            minHeight: 36,
            padding: '4px 16px',
            borderBottom: ft.hair,
            background: R.bgSubtle,
            flexWrap: 'wrap',
            flex: '0 0 auto',
          }}
        >
          <span style={{ ...label, fontSize: 10, marginRight: 2 }}>Фильтры:</span>

          {hiddenColIds.size > 0 && (
            <button
              style={{
                ...chip(true),
                background: 'rgba(230, 126, 34, 0.12)',
                borderColor: 'rgba(230, 126, 34, 0.35)',
                color: '#d35400',
                display: 'inline-flex',
                alignItems: 'center',
              }}
              onClick={unhideAllColumns}
              title="Нажмите, чтобы показать все скрытые колонки"
            >
              <EyeOff size={11} style={{ marginRight: 4 }} />
              Скрыто колонок: {hiddenColIds.size} <X size={11} style={{ marginLeft: 4 }} />
            </button>
          )}

          {query.trim() && (
            <button
              style={chip(true)}
              onClick={() => {
                setQuery('');
                setShowSearch(false);
              }}
              title="Нажмите, чтобы сбросить поиск"
            >
              Поиск: «{query.trim()}» <X size={11} style={{ marginLeft: 4 }} />
            </button>
          )}

          {sort && (
            <button
              style={chip(false)}
              onClick={() => setSort(null)}
              title="Нажмите, чтобы сбросить сортировку"
            >
              Сортировка: {getColLabel(sort.colId)} {sort.dir === 'asc' ? '↑ (А-Я)' : '↓ (Я-А)'}
              <X size={11} style={{ marginLeft: 4 }} />
            </button>
          )}

          {[...filters.entries()].map(([colId, vals]) => (
            <button
              key={colId}
              style={chip(false)}
              onClick={() => setFilter(colId, null)}
              title={[...vals].join(', ')}
            >
              {getColLabel(colId)}:{' '}
              {vals.size === 1 ? [...vals][0] || 'пусто' : `${vals.size} знач.`}
              <X size={11} style={{ marginLeft: 4 }} />
            </button>
          ))}

          <span style={{ marginLeft: 'auto', fontSize: 11.5, color: R.textMuted }}>
            Показано <strong>{view.length}</strong> из {table?.records.length ?? 0} строк
          </span>
        </div>
      )}

      {/* ---------- СЕТКА ТАБЛИЦЫ С ПЛАВНОЙ ПРОКРУТКОЙ (РОЛЛ) ---------- */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* ВСПЛЫВАЮЩАЯ ПАНЕЛЬ ПОИСКА (Ctrl+F) */}
        {showSearch && (
          <div
            style={{
              position: 'absolute',
              top: 8,
              right: 16,
              zIndex: 50,
              background: R.bgPanel,
              border: `1px solid ${R.border}`,
              borderRadius: 6,
              boxShadow: shadow.dropdown,
              padding: '6px 8px',
              display: 'flex',
              gap: 6,
              alignItems: 'center',
              animation: 'rtIn .15s ease',
            }}
          >
            <Search size={13} color={R.textMuted} />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setShowSearch(false);
                  setQuery('');
                  setSearchColId(null);
                }
              }}
              placeholder="Поиск по таблице…"
              style={{
                width: 220,
                padding: '4px 6px',
                fontSize: 12.5,
                background: 'transparent',
                border: 'none',
                color: R.text,
              }}
            />

            <span style={{ width: 1, height: 18, background: R.border }} />

            <span style={{ width: 160 }} title="Искать в конкретной колонке">
              <Select
                compact
                value={searchColId ?? '__all__'}
                onChange={(v) => setSearchColId(v === '__all__' ? null : v)}
                options={[
                  { value: '__all__', label: 'По всем колонкам' },
                  ...(table?.columns ?? []).map((c) => ({
                    value: c.fields.colId,
                    label: c.fields.label,
                  })),
                ]}
              />
            </span>

            <span style={{ fontSize: 11, color: R.textMuted, minWidth: 28, textAlign: 'center' }}>
              {query ? `${view.length}` : ''}
            </span>

            <button
              onClick={() => {
                setShowSearch(false);
                setQuery('');
                setSearchColId(null);
              }}
              title="Закрыть (Esc)"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 20,
                height: 20,
                border: 'none',
                background: 'transparent',
                color: R.textMuted,
                cursor: 'pointer',
              }}
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* СЕТКА GRID */}
        {!table ? (
          <div style={{ padding: 40, textAlign: 'center', color: R.textMuted }}>
            В документе нет доступных таблиц.
          </div>
        ) : (
          <Grid
            columns={table.columns}
            records={view}
            editable={false}
            hiddenColIds={hiddenColIds}
            onToggleHideColumn={toggleHideColumn}
            onUnhideAllColumns={unhideAllColumns}
            onUnhideColumns={unhideColumns}
            onPatch={() => {}}
            onCreate={() => {}}
            computed={computed}
            styles={styles}
            merges={table.merges}
            conditional={table.conditional}
            colWidths={colWidths}
            rowHeights={rowHeights}
            sort={sort}
            onSort={setSort}
            filters={filters}
            valuesFor={valuesFor}
            onFilter={setFilter}
          />
        )}
      </div>

      {/* ---------- СТАТУСНАЯ СТРОКА ---------- */}
      <footer
        style={{
          height: 28,
          background: R.bgSubtle,
          borderTop: ft.hair,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 16px',
          fontSize: 11.5,
          color: R.textMuted,
          flex: '0 0 auto',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <strong style={{ color: R.text }}>Redwood Tables</strong>
          <span>•</span>
          <span>Просмотр реестра</span>
        </div>

        <div>
          {isFilteredOrSorted ? (
            <span>
              Показано <strong>{view.length}</strong> из {table?.records.length ?? 0} строк ·{' '}
              {table?.columns.length ?? 0} колонок
            </span>
          ) : (
            <span>
              Всего <strong>{table?.records.length ?? 0}</strong> строк · {table?.columns.length ?? 0} колонок
            </span>
          )}
        </div>
      </footer>
    </div>
  );
}
